import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Tag,
  Text,
  Button,
  CircleRating,
  CheckIcon,
  CloseIcon,
  Tooltip,
  ArrowRightIcon,
  ChartIcon,
  SparksIcon,
  InfoIcon,
  Switch,
  ForumIcon,
  TrashIcon,
} from '@mercai/clever'

import { formatDate, formatDateWithTime, getContrastTextColor } from '@/helpers'

import { ROUTES_ADMIN } from '@/router'

import {
  useAIEvaluationConversation,
  useAIUpdateConversation,
  QUERIES_ADMIN,
} from '@/services'

import {
  TableColumnCreatedByProps,
  TableColumnStatusProps,
  TableColumnGeneratedDataProps,
  TableColumnAttendedAtProps,
  TableColumnMessagesProps,
  TableColumnEvaluationProps,
  TableColumnScoreProps,
  TableColumnJtbdsProps,
  TableColumnCreatedAtProps,
  TableColumnDevelopmentActionsProps,
  TableColumnAcquisitionActionsProps,
  TableColumnTagsProps,
} from './table-columns.type'

import styles from './table-columns.module.css'

export const TableColumnCreatedBy = ({ record }: TableColumnCreatedByProps) => {
  const { t } = useTranslation('enums')

  return (
    <div className={styles['created-by-wrapper']}>
      <Text>{record?.createdBy}</Text>
      <Tag model="outline" size="small" variant="basic">
        {t(`conversation_role_${record?.role}`)}
      </Tag>
    </div>
  )
}

export const TableColumnStatus = ({ value }: TableColumnStatusProps) => {
  return (
    <Tag variant={value === 'active' ? 'success' : 'danger'} model="outline">
      {value}
    </Tag>
  )
}

export const TableColumnGeneratedData = ({
  record,
}: TableColumnGeneratedDataProps) => {
  const { t } = useTranslation('screenAiCenter')

  const isRfq = record?.extraData?.rfq?.generatedData?.length > 0

  if (!isRfq) {
    return (
      <Text className={styles['extra-data-text']}>
        {t('conversationTableGeneratedDataNone')}
      </Text>
    )
  }

  return (
    <div className={styles['link-wrapper']}>
      {record?.extraData?.rfq?.generatedData?.map((rfq) => (
        <Text
          variant="link"
          key={rfq}
          href={`${ROUTES_ADMIN.BUSINESS.RFQ.fullPath()}?search=${rfq}`}
          className={styles['link-rfq']}
          target="_blank"
        >
          {rfq}
        </Text>
      ))}
    </div>
  )
}

export const TableColumnAttendedAt = ({
  value,
  record,
}: TableColumnAttendedAtProps) => {
  const { t } = useTranslation('screenAiCenter')
  const queryClient = useQueryClient()

  const {
    mutateAsync: updateWhatsappConversation,
    isPending: isPendingUpdateWhatsappConversation,
  } = useAIUpdateConversation()

  const onHandleUpdateWhatsappConversation = async (uuid: string) => {
    const response = await updateWhatsappConversation({
      uuid,
      attendedAt: new Date().toISOString(),
    })

    if (response?.success) {
      queryClient.refetchQueries({
        queryKey: [QUERIES_ADMIN.AI_CONVERSATION.module],
      })
    }
  }

  return (
    <div className={styles['column-attended-by-acquisition']}>
      <Switch
        checked={Boolean(value)}
        onChange={() => onHandleUpdateWhatsappConversation(record?.uuid)}
        disabled={isPendingUpdateWhatsappConversation}
      />

      {value
        ? formatDate(value)
        : t('conversationTableattendedByAcquisitionNo')}
    </div>
  )
}

export const TableColumnMessages = ({ value }: TableColumnMessagesProps) => {
  return <span>{value?.length} messages</span>
}

export const TableColumnEvaluation = ({
  record,
}: TableColumnEvaluationProps) => {
  const jtbdCompleted = record?.extraData?.evaluation?.isJtbdCompleted
  const evaluation = record?.extraData?.evaluation?.generalNote

  return (
    <div className={styles['evaluation-wrapper']}>
      <CircleRating rating={+(evaluation || 0)} suffix="" />

      {jtbdCompleted ? (
        <CheckIcon className={styles.check} />
      ) : (
        <CloseIcon className={styles.close} />
      )}
    </div>
  )
}

export const TableColumnScore = ({ record }: TableColumnScoreProps) => {
  const evaluation = record?.extraData?.leadQualifier?.finalScoring?.totalScore
  const maxTotalScore =
    record?.extraData?.leadQualifier?.finalScoring?.maxTotalScore

  const summary = record?.extraData?.leadQualifier?.finalScoring?.summary

  return (
    <div className={styles['evaluation-wrapper']}>
      <CircleRating
        rating={+(evaluation || 0)}
        maxRating={maxTotalScore}
        suffix=""
      />

      {summary && (
        <Tooltip text={summary} widthText="20rem" orientation="bottom">
          <InfoIcon />
        </Tooltip>
      )}
    </div>
  )
}

export const TableColumnJtbds = ({ record }: TableColumnJtbdsProps) => {
  const { t } = useTranslation('screenAiCenter')
  const agentEvaluations = record?.extraData?.evaluation?.agentEvaluations

  return (
    <div className={styles['agents-wrapper']}>
      {agentEvaluations?.map((agent) => (
        <Tooltip
          key={agent.agent}
          widthText="10rem"
          text={
            agent.isJtbdCompleted
              ? t('conversationTableJtbdTrueTooltip')
              : t('conversationTableJtbdFalseTooltip')
          }
        >
          <Tag
            key={agent.agent}
            variant={agent.isJtbdCompleted ? 'success' : 'danger'}
            model="outline"
            size="small"
          >
            {agent.agent}
          </Tag>
        </Tooltip>
      ))}
    </div>
  )
}

export const TableColumnCreatedAt = ({ value }: TableColumnCreatedAtProps) => {
  if (!value) {
    return <span>-</span>
  }

  const [date, time] = formatDateWithTime(value)?.split(' ')

  return (
    <div className={styles['created-at-wrapper']}>
      <Text variant="title-base">{date}</Text>
      <Text variant="text-small">{time}</Text>
    </div>
  )
}

export const TableColumnTags = ({ record }: TableColumnTagsProps) => {
  return (
    <div className={styles.tags}>
      {record?.tags?.map((tag) => (
        <Tag
          key={tag.uuid}
          variant="secondary"
          size="small"
          style={{
            backgroundColor: tag.color,
            color: getContrastTextColor(tag.color),
          }}
        >
          {tag.slug}
        </Tag>
      ))}
    </div>
  )
}

export const TableColumnDevelopmentActions = ({
  record,
  onHandleRegenerateConversation = () => {},
  onHandleEditRegister,
  onHandleShowRegister,
  onHandleDeleteRegister,
}: TableColumnDevelopmentActionsProps) => {
  const queryClient = useQueryClient()

  const {
    mutateAsync: evaluateConversation,
    isPending: isPendingEvaluateConversation,
  } = useAIEvaluationConversation()

  const onHandleEvaluateConversation = async (uuid: string) => {
    const response = await evaluateConversation(uuid)

    if (response?.success) {
      queryClient.refetchQueries({
        queryKey: [QUERIES_ADMIN.AI_CONVERSATION.module],
      })
    }
  }

  return (
    <div className={styles['action-buttons']}>
      <Button
        variant="secondary"
        model="square"
        onClick={() => onHandleEditRegister(record.uuid || '')}
      >
        <ForumIcon />
      </Button>

      <Button
        variant="secondary"
        model="square"
        onClick={() => onHandleRegenerateConversation(record.uuid || '')}
      >
        <SparksIcon />
      </Button>
      <Button
        variant="secondary"
        model="square"
        onClick={() => onHandleEvaluateConversation(record.uuid || '')}
        disabled={isPendingEvaluateConversation}
      >
        <ChartIcon />
      </Button>

      <Button
        variant="primary"
        onClick={() => onHandleShowRegister(record.uuid || '')}
        model="square"
      >
        <ArrowRightIcon />
      </Button>

      <Button
        variant="danger"
        model="square"
        onClick={() => onHandleDeleteRegister(record.uuid || '')}
      >
        <TrashIcon />
      </Button>
    </div>
  )
}

export const TableColumnAcquisitionActions = ({
  record,
  onHandleShowRegister,
  onHandleEditRegister,
}: TableColumnAcquisitionActionsProps) => {
  return (
    <div className={styles['action-buttons']}>
      <Button
        variant="secondary"
        model="square"
        onClick={() => onHandleEditRegister(record.uuid || '')}
      >
        <ForumIcon />
      </Button>

      <Button
        variant="primary"
        onClick={() => onHandleShowRegister(record.uuid || '')}
        model="square"
      >
        <ArrowRightIcon />
      </Button>
    </div>
  )
}
